import {
    Combobox,
    Group,
    Input,
    InputBase,
    Text,
    useCombobox,
    type ComboboxProps,
    type MantineStyleProps,
} from '@mantine/core';
import { iso2ToIoc } from '@scoring/utils';
import { getAllCountries } from 'countries-and-timezones';
import React, { memo, useState } from 'react';
import { CountryFlagDisplay } from '../display/CountryFlagDisplay';

interface CountryInputProps extends Omit<ComboboxProps, 'data'> {
    value?: string;
    onChange?: (value: string | null) => void;
    size?: string;
    error?: React.ReactNode;
    label?: React.ReactNode;
    disabled?: boolean;
    w?: MantineStyleProps['w'];
}

export interface CountryItem {
    value: string;
    label: string;
    iso2: string;
    ioc: string;
}

// The picker emits IOC codes; countries without an IOC equivalent are dropped.
const ALL_COUNTRIES: CountryItem[] = Object.values(getAllCountries())
    .map((country): CountryItem | null => {
        const ioc = iso2ToIoc(country.id);
        if (!ioc) return null;
        return { value: ioc, label: country.name, iso2: country.id, ioc };
    })
    .filter((c): c is CountryItem => c !== null)
    .sort((a, b) => a.label.localeCompare(b.label));

// Shared with anything that needs a country choice list keyed by IOC code (e.g. filter panels).
// eslint-disable-next-line react-refresh/only-export-components
export const countryOptions: readonly CountryItem[] = ALL_COUNTRIES;

// 💨 Memo für Item, damit Flags nicht bei jedem Render neu gebaut werden
const CountryComboboxItem = memo(({ label, value }: { label: string; value: string }) => (
    <Group flex="1" gap="md">
        <CountryFlagDisplay width={20} iocCode={value} />
        <Text size="xs">{value}</Text>
        <Text size="xs">{label}</Text>
    </Group>
));

export const CountryInput = ({
    value,
    onChange,
    size = 'xs',
    error,
    label = 'Country',
    disabled,
    ...comboBoxProps
}: CountryInputProps) => {
    const combobox = useCombobox({ scrollBehavior: 'smooth' });
    const [search, setSearch] = useState('');
    const [selectedValue, setSelectedValue] = useState<string | null>(value ?? null);
    const [isEditing, setIsEditing] = useState(false);

    // Sync internal state when the controlled value prop changes (during render, per React docs)
    const [prevValue, setPrevValue] = useState(value);
    if (value !== prevValue) {
        setPrevValue(value);
        const selected = ALL_COUNTRIES.find((c) => c.value === value);
        if (selected) {
            setSelectedValue(selected.value);
            setSearch(selected.value);
        }
    }

    const handleSelect = (val: string | null) => {
        const selected = ALL_COUNTRIES.find((c) => c.value === val);
        setSelectedValue(val);
        setSearch(selected?.value ?? '');
        onChange?.(val);
        setIsEditing(false);
        combobox.closeDropdown();
    };

    const filteredCountries = search
        ? ALL_COUNTRIES.filter(
              (c) =>
                  c.label.toLowerCase().includes(search.toLowerCase().trim()) ||
                  c.value.toLowerCase().includes(search.toLowerCase().trim())
          )
        : [];

    const selectedOption = ALL_COUNTRIES.find((c) => c.value === selectedValue);

    return (
        <Combobox
            store={combobox}
            onOptionSubmit={handleSelect}
            size={size}
            withinPortal={false}
            withArrow
            disabled={disabled}
            {...comboBoxProps}
        >
            <Combobox.Target>
                {isEditing ? (
                    <InputBase
                        disabled={disabled}
                        size={size}
                        error={error}
                        component="input"
                        value={search}
                        autoFocus
                        label={label}
                        onChange={(e) => {
                            setSearch(e.currentTarget.value);
                            combobox.openDropdown();
                        }}
                        onFocus={(e) => {
                            e.currentTarget.select();
                            combobox.openDropdown();
                        }}
                        onBlur={() => setIsEditing(false)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                if (filteredCountries.length === 1) {
                                    handleSelect(filteredCountries[0].value);
                                } else if (filteredCountries.length > 1) {
                                    const exact = filteredCountries.find(
                                        (c) => c.value.toLowerCase() === search.toLowerCase().trim()
                                    );
                                    if (exact) handleSelect(exact.value);
                                }
                            }
                        }}
                        placeholder="Select country..."
                        rightSection={<Combobox.Chevron />}
                        rightSectionPointerEvents="none"
                    />
                ) : (
                    <InputBase
                        size={size}
                        error={error}
                        component="button"
                        type="button"
                        disabled={disabled}
                        label={label}
                        pointer
                        onClick={() => {
                            setIsEditing(true);
                            setTimeout(() => combobox.openDropdown(), 0);
                        }}
                        rightSection={<Combobox.Chevron />}
                        rightSectionPointerEvents="none"
                    >
                        {selectedOption ? (
                            <CountryComboboxItem {...selectedOption} />
                        ) : (
                            <Input.Placeholder>Select country...</Input.Placeholder>
                        )}
                    </InputBase>
                )}
            </Combobox.Target>

            <Combobox.Dropdown>
                <Combobox.Options mah={200} style={{ overflowY: 'auto' }}>
                    {filteredCountries.length > 0 ? (
                        filteredCountries.map((c) => (
                            <Combobox.Option key={c.value} value={c.value}>
                                <CountryComboboxItem {...c} />
                            </Combobox.Option>
                        ))
                    ) : (
                        <Combobox.Empty>No countries found</Combobox.Empty>
                    )}
                </Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    );
};
